import React, { Fragment, Children, type ReactNode } from 'react';
import { useIntl, FormattedDate } from 'react-intl';
import { Html } from 'components';

// Component
import { Tag, WarningAltFilled, Information, AddAlt, Tools, Event, Unlink } from '@carbon/icons-react';
import { AutolinkHeader } from '../AutolinkHeader';
import { H2, H3, Ul, Li } from '../markdown';
import { DefinitionTooltip } from '@carbon/react';

// CSS
import cn from 'clsx';
import * as css from './Release.module.scss';

function remarkChildrenLite(children: ReactNode): ReactNode[] {
    const processedChildren: ReactNode[] = [];

    Children.forEach(children, child => {
        const key = processedChildren.length;
        if (typeof child === 'string') {
            processedChildren.push(
                <Html
                    key={key}
                    container="span"
                    source={
                        child
                            // `lorem` > `<code>lorem</code>`
                            .replace(/`([^`]+)`/g, '<code>$1</code>')
                            // `[text](link)` with `<a href="link">text</a>`
                            .replace(/\[([^\]]+)]\(([^)]+)\)/g, '<a href="$2">$1</a>')
                            // `**text**` with `<strong>text</strong>`
                            .replace(/\*{2}([^*]+)\*{2}/g, '<em>$1</em>')
                            // `_text_` with `<em>text</em>`
                            .replace(/_([^_]+)_/g, '<em>$1</em>')
                            // `~text~` with `<del>text</del>`
                            .replace(/~([^~]+)~/g, '<del>$1</del>')
                        //
                    }
                />,
            );
            return;
        }

        processedChildren.push(<Fragment key={key} children={child} />);
    });

    return processedChildren;
}

export type ReleaseProps = {
    title: string;
    date?: [yyyy: number, mm: number, dd: number];
    children: ReactNode;
};
export function Release({ title, date, children }: ReleaseProps) {
    const { formatMessage } = useIntl();

    let $date: ReactNode;
    if (date) {
        const [yyyy, mm, dd] = date;
        $date = (
            <time
                className={css.headerDate}
                dateTime={`${yyyy}-${mm}-${dd}`}
                title={formatMessage({ defaultMessage: 'Release date' })}
            >
                <Event />
                <FormattedDate value={new Date(yyyy, mm - 1, dd)} year="numeric" month="long" day="2-digit" />
            </time>
        );
    }

    return (
        <section className={css.root}>
            <AutolinkHeader is={H2} className={css.h2}>
                <Tag />
                &nbsp;
                {title}
            </AutolinkHeader>
            {$date}

            {children}
        </section>
    );
}

export type ChangesProps = {
    title: string;
    children: ReactNode;
};
export function ReleaseChanges({ title, children }: ChangesProps) {
    return (
        <Fragment>
            <AutolinkHeader is={H3} children={title} className={css.h3} />
            <Ul children={children} />
        </Fragment>
    );
}

export type ChangeProps = {
    type: 'fix' | 'feature' | 'info' | 'warn' | 'breaking';
    children: ReactNode;
};
export function ReleaseChange({ type, children }: ChangeProps) {
    const { formatMessage } = useIntl();

    let icon: ReactNode;
    let tooltipText: ReactNode;
    switch (type) {
        case 'fix':
            icon = <Tools />;
            tooltipText = formatMessage({ defaultMessage: 'Fix' });
            break;

        case 'feature':
            icon = <AddAlt />;
            tooltipText = formatMessage({ defaultMessage: 'Feature' });
            break;

        case 'info':
            icon = <Information />;
            tooltipText = formatMessage({ defaultMessage: 'Info' });
            break;

        case 'warn':
            icon = <WarningAltFilled />;
            tooltipText = formatMessage({ defaultMessage: 'Warning' });
            break;

        case 'breaking':
            icon = <Unlink />;
            tooltipText = formatMessage({ defaultMessage: 'Breaking' });
            break;

        default: {
            const _: never = type;
        }
    }

    return (
        <Li className={cn(css.listItem, css[type])}>
            <DefinitionTooltip
                align="left"
                openOnHover
                children={icon}
                definition={tooltipText}
                style={{
                    padding: 0,
                    lineHeight: 0,
                    border: 'none',
                    verticalAlign: 'middle',
                }}
            />
            &nbsp;
            <span children={remarkChildrenLite(children)} />
        </Li>
    );
}
